import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  sampleSubpaths,
  samplePath,
  findNearestPoint,
  findNearestPointAcrossSubpaths,
  findNearestPointAcrossPieces,
  splitAtIndices,
  validateSplitDistance,
  cutShapeAtIndices,
  pointsToClosedD,
} from "../helpers/splitPathToRails";

export const DEFAULT_PASS = {
  stitchType: "auto_fill",
  fill_method: "auto_fill",
  angle: null,
  row_spacing_mm: 0.2,
  max_stitch_length_mm: 3.0,
  staggers: 4,
  expand_mm: 0,
  fill_underlay: true,
  underlay_type: "auto",
  fill_underlay_row_spacing_mm: 3.0,
  fill_underlay_angle: null,
  pull_compensation_mm: 0.2,
  zigzag_spacing_mm: 0.4,
  push_compensation_mm: 0,
  split_method: "staggered",
  split_staggers: 4,
  short_stitch_distance_mm: 0.25,
  short_stitch_inset: 15,
  satin_underlay_profile: "auto",
  contour_underlay_inset_mm: 0.4,
  zigzag_underlay_spacing_mm: 3.0,
  reverse_rails: "automatic",
  swap_satin_rails: false,
  running_stitch_length_mm: 2.5,
  bean_stitch_repeats: 0,
  randomize_stitch_length: false,
};

const DEFAULT_LAYER_SETTINGS = {
  passes: [{ ...DEFAULT_PASS, id: "pass_0" }],
  activePassIndex: 0,
};

function ensurePasses(settings) {
  if (settings?.passes) return settings;
  const { passes, activePassIndex, ...flat } = settings || {};
  const hasFields = Object.keys(flat).length > 0;
  return {
    passes: [{ ...DEFAULT_PASS, id: "pass_0", ...(hasFields ? flat : {}) }],
    activePassIndex: 0,
  };
}

let passIdCounter = 0;

export const STITCH_TYPE_OPTIONS = [
  { value: "auto_fill", label: "Fill", icon: "▰", description: "Auto-routed fill stitch for solid areas" },
  { value: "satin", label: "Satin", icon: "⫽", description: "Satin column stitch — requires two rails" },
  { value: "running", label: "Running", icon: "╌", description: "Single-line running stitch along the path" },
];

export default function useEmbroideryMode(numLayers) {
  const [embroideryMode, setEmbroideryMode] = useState(false);
  const [layerStitchSettings, setLayerStitchSettings] = useState({});
  const [layerRails, setLayerRails] = useState({});
  // Per-layer cut-tool lines (SVG viewBox path d strings), persisted with stitch state.
  const [layerCutMarks, setLayerCutMarks] = useState({});
  const [selectedHoop, setSelectedHoop] = useState(0);
  const [targetWidth, setTargetWidth] = useState(100);
  const [targetHeight, setTargetHeight] = useState(100);
  const [activeRailDrawing, setActiveRailDrawing] = useState(null);
  const [splitMode, setSplitMode] = useState(null);
  const [splitHoverPoint, setSplitHoverPoint] = useState(null);
  const splitSubpathCache = useRef(null);
  const railIdCounter = useRef(0);

  const [cutMode, setCutMode] = useState(null);
  const [cutHoverPoint, setCutHoverPoint] = useState(null);
  const cutHistoryRef = useRef([]);
  const recordStitchHistoryRef = useRef(null);

  const getLayerSettings = useCallback((layerIndex) => {
    const raw = layerStitchSettings[layerIndex];
    return ensurePasses(raw || { ...DEFAULT_LAYER_SETTINGS });
  }, [layerStitchSettings]);

  const updateLayerSettings = useCallback((layerIndex, updates) => {
    setLayerStitchSettings((prev) => {
      const existing = ensurePasses(prev[layerIndex]);
      const activeIdx = existing.activePassIndex || 0;
      const newPasses = existing.passes.map((p, i) =>
        i === activeIdx ? { ...p, ...updates } : p
      );
      return { ...prev, [layerIndex]: { ...existing, passes: newPasses } };
    });
  }, []);

  const setLayerStitchType = useCallback((layerIndex, stitchType, { railsUpdater, passPatch } = {}) => {
    const record = recordStitchHistoryRef.current;
    if (record) {
      record({
        settingsUpdater: (prev) => {
          const existing = ensurePasses(prev[layerIndex]);
          const activeIdx = existing.activePassIndex || 0;
          const newPasses = existing.passes.map((p, i) => {
            if (i !== activeIdx) return p;
              const patch = { stitchType, ...(passPatch || {}) };
            if (stitchType !== "satin" && stitchType !== "running") {
              patch.fill_method = stitchType;
            }
            return { ...p, ...patch };
          });
          return { ...prev, [layerIndex]: { ...existing, passes: newPasses } };
        },
        railsUpdater: railsUpdater ?? (stitchType !== "satin"
          ? (prev) => {
              const next = { ...prev };
              delete next[layerIndex];
              return next;
            }
          : undefined),
      });
      return;
    }
    // Fallback before history is initialized (should not happen in practice).
    updateLayerSettings(layerIndex, { stitchType, ...(passPatch || {}) });
    if (stitchType !== "satin") {
      setLayerRails((prev) => {
        const next = { ...prev };
        delete next[layerIndex];
        return next;
      });
    }
  }, [updateLayerSettings]);

  const addPass = useCallback((layerIndex, passDefaults) => {
    setLayerStitchSettings((prev) => {
      const existing = ensurePasses(prev[layerIndex]);
      const newPass = { ...DEFAULT_PASS, id: `pass_${++passIdCounter}`, ...(passDefaults || {}) };
      const newPasses = [...existing.passes, newPass];
      return {
        ...prev,
        [layerIndex]: { ...existing, passes: newPasses, activePassIndex: newPasses.length - 1 },
      };
    });
  }, []);

  const removePass = useCallback((layerIndex, passIndex) => {
    setLayerStitchSettings((prev) => {
      const existing = ensurePasses(prev[layerIndex]);
      if (existing.passes.length <= 1) return prev;
      const newPasses = existing.passes.filter((_, i) => i !== passIndex);
      const newActive = Math.min(existing.activePassIndex, newPasses.length - 1);
      return {
        ...prev,
        [layerIndex]: { ...existing, passes: newPasses, activePassIndex: newActive },
      };
    });
  }, []);

  const updatePassSettings = useCallback((layerIndex, passIndex, updates) => {
    setLayerStitchSettings((prev) => {
      const existing = ensurePasses(prev[layerIndex]);
      const newPasses = existing.passes.map((p, i) =>
        i === passIndex ? { ...p, ...updates } : p
      );
      return { ...prev, [layerIndex]: { ...existing, passes: newPasses } };
    });
  }, []);

  const setActivePass = useCallback((layerIndex, passIndex) => {
    setLayerStitchSettings((prev) => {
      const existing = ensurePasses(prev[layerIndex]);
      return { ...prev, [layerIndex]: { ...existing, activePassIndex: passIndex } };
    });
  }, []);

  const setLayerRailPath = useCallback((layerIndex, railIndex, pathD) => {
    setLayerRails((prev) => {
      const existing = prev[layerIndex] || {};
      return {
        ...prev,
        [layerIndex]: { ...existing, [`rail${railIndex}`]: pathD },
      };
    });
  }, []);

  const getRails = useCallback((layerIndex) => {
    return layerRails[layerIndex] || null;
  }, [layerRails]);

  const setLayerCutMarksForLayer = useCallback((layerIndex, marks) => {
    setLayerCutMarks((prev) => {
      const next = { ...prev };
      if (!marks?.length) delete next[layerIndex];
      else next[layerIndex] = marks;
      return next;
    });
  }, []);

  const startRailDrawing = useCallback((layerIndex, railIndex) => {
    setActiveRailDrawing({ layerIndex, railIndex, points: [] });
  }, []);

  const addRailPoint = useCallback((x, y) => {
    setActiveRailDrawing((prev) => {
      if (!prev) return null;
      return { ...prev, points: [...prev.points, { x, y }] };
    });
  }, []);

  const finishRailDrawing = useCallback(() => {
    if (!activeRailDrawing || activeRailDrawing.points.length < 2) {
      setActiveRailDrawing(null);
      return;
    }
    const { layerIndex, railIndex, points } = activeRailDrawing;
    const d = "M" + points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" L");
    setLayerRailPath(layerIndex, railIndex, d);
    setActiveRailDrawing(null);
  }, [activeRailDrawing, setLayerRailPath]);

  const cancelRailDrawing = useCallback(() => {
    setActiveRailDrawing(null);
  }, []);

  const splitRailsCallback = useRef(null);

  const startSplitMode = useCallback((layerIndex, pathD, onRailsCreated) => {
    const subpathData = sampleSubpaths(pathD, 400);
    splitSubpathCache.current = subpathData;
    splitRailsCallback.current = onRailsCreated || null;
    setSplitMode({ layerIndex, pathD, point1: null });
  }, []);

  const getSplitSubpaths = useCallback(() => {
    return splitSubpathCache.current;
  }, []);

  const setSplitPoint = useCallback((clickX, clickY) => {
    const subpathData = splitSubpathCache.current;
    if (!subpathData || !splitMode) return;

    const hit = findNearestPointAcrossSubpaths(subpathData, clickX, clickY);
    if (!hit) return;

    if (!splitMode.point1) {
      setSplitMode((prev) => ({
        ...prev,
        point1: {
          x: hit.point.x,
          y: hit.point.y,
          index: hit.index,
          subpathIndex: hit.subpathIndex,
        },
      }));
      return;
    }

    if (hit.subpathIndex !== splitMode.point1.subpathIndex) {
      console.warn("[SplitMode] Points are on different subpaths:", hit.subpathIndex, "vs", splitMode.point1.subpathIndex);
      return;
    }

    const sp = subpathData[hit.subpathIndex];
    if (!validateSplitDistance(sp.points, splitMode.point1.index, hit.index)) {
      console.warn("[SplitMode] Points too close:", splitMode.point1.index, "vs", hit.index, "of", sp.points.length);
      return;
    }

    const rails = splitAtIndices(sp.points, splitMode.point1.index, hit.index);
    if (!rails) {
      console.warn("[SplitMode] splitAtIndices returned null");
      return;
    }

    const { layerIndex } = splitMode;

    if (splitRailsCallback.current) {
      splitRailsCallback.current(layerIndex, rails);
    } else {
      setLayerRails((prev) => {
        const existing = prev[layerIndex] || {};
        const pairs = existing.pairs ? [...existing.pairs] : [];
        pairs.push({ rail0: rails.rail0, rail1: rails.rail1 });
        return { ...prev, [layerIndex]: { ...existing, pairs } };
      });
    }

    // Stay in split mode so the tool runs continuously — reset just the first
    // point and keep the cached subpaths so the user can immediately mark the
    // next satin column without reselecting the tool. They exit via Done/Cancel.
    setSplitMode((prev) => (prev ? { ...prev, point1: null } : prev));
    setSplitHoverPoint(null);
  }, [splitMode]);

  const updateSplitHover = useCallback((clickX, clickY) => {
    const subpathData = splitSubpathCache.current;
    if (!subpathData || !splitMode) {
      setSplitHoverPoint(null);
      return;
    }
    const hit = findNearestPointAcrossSubpaths(subpathData, clickX, clickY);
    if (hit) {
      setSplitHoverPoint({ x: hit.point.x, y: hit.point.y });
    }
  }, [splitMode]);

  const cancelSplitMode = useCallback(() => {
    setSplitMode(null);
    setSplitHoverPoint(null);
    splitSubpathCache.current = null;
  }, []);

  const removeLastRailPair = useCallback((layerIndex) => {
    setLayerRails((prev) => {
      const existing = prev[layerIndex];
      if (!existing?.pairs?.length) return prev;
      const pairs = existing.pairs.slice(0, -1);
      if (pairs.length === 0) {
        const next = { ...prev };
        delete next[layerIndex];
        return next;
      }
      return { ...prev, [layerIndex]: { ...existing, pairs } };
    });
  }, []);

  const removeRailPair = useCallback((layerIndex, pairIdx) => {
    setLayerRails((prev) => {
      const existing = prev[layerIndex];
      if (!existing?.pairs?.length) return prev;
      const pairs = existing.pairs.filter((_, i) => i !== pairIdx);
      if (pairs.length === 0) {
        const next = { ...prev };
        delete next[layerIndex];
        return next;
      }
      return { ...prev, [layerIndex]: { ...existing, pairs } };
    });
  }, []);

  // ── Cut Mode ──────────────────────────────────────────────────────

  const startCutMode = useCallback((layerIndex, pathD) => {
    const subpaths = sampleSubpaths(pathD, 500);
    const pieces = subpaths.map((sp) => ({
      points: sp.points,
      d: pointsToClosedD(sp.points),
    }));
    if (pieces.length === 0) return;
    cutHistoryRef.current = [pieces];
    setCutMode({ layerIndex, originalPathD: pathD, pieces, point1: null });
    setCutHoverPoint(null);
  }, []);

  const setCutPoint = useCallback((clickX, clickY) => {
    if (!cutMode) return;
    const { pieces } = cutMode;

    if (!cutMode.point1) {
      const hit = findNearestPointAcrossPieces(pieces, clickX, clickY);
      if (!hit) return;
      setCutMode((prev) => ({
        ...prev,
        point1: { pieceIndex: hit.pieceIndex, index: hit.index, x: hit.point.x, y: hit.point.y },
      }));
      return;
    }

    const piece = pieces[cutMode.point1.pieceIndex];
    const hit2 = findNearestPoint(piece.points, clickX, clickY);
    if (!hit2) return;

    if (!validateSplitDistance(piece.points, cutMode.point1.index, hit2.index, 0.05)) {
      console.warn("[CutMode] Points too close");
      return;
    }

    const result = cutShapeAtIndices(piece.points, cutMode.point1.index, hit2.index);
    if (!result) {
      console.warn("[CutMode] cutShapeAtIndices returned null");
      return;
    }

    const newPieces = [...pieces];
    newPieces.splice(cutMode.point1.pieceIndex, 1,
      { points: result.pieceA, d: pointsToClosedD(result.pieceA) },
      { points: result.pieceB, d: pointsToClosedD(result.pieceB) },
    );

    cutHistoryRef.current.push(newPieces);
    setCutMode((prev) => ({ ...prev, pieces: newPieces, point1: null }));
    setCutHoverPoint(null);
  }, [cutMode]);

  const updateCutHover = useCallback((clickX, clickY) => {
    if (!cutMode) {
      setCutHoverPoint(null);
      return;
    }
    if (cutMode.point1) {
      const piece = cutMode.pieces[cutMode.point1.pieceIndex];
      const hit = findNearestPoint(piece.points, clickX, clickY);
      if (hit) setCutHoverPoint({ x: hit.point.x, y: hit.point.y });
    } else {
      const hit = findNearestPointAcrossPieces(cutMode.pieces, clickX, clickY);
      if (hit) setCutHoverPoint({ x: hit.point.x, y: hit.point.y });
    }
  }, [cutMode]);

  const undoLastCut = useCallback(() => {
    if (!cutMode) return;
    const history = cutHistoryRef.current;
    if (history.length <= 1) return;
    history.pop();
    const prev = history[history.length - 1];
    setCutMode((m) => ({ ...m, pieces: prev, point1: null }));
    setCutHoverPoint(null);
  }, [cutMode]);

  const finishCutMode = useCallback(() => {
    if (!cutMode) return null;
    const { layerIndex, pieces } = cutMode;
    setCutMode(null);
    setCutHoverPoint(null);
    cutHistoryRef.current = [];
    return { layerIndex, pieces };
  }, [cutMode]);

  const cancelCutMode = useCallback(() => {
    setCutMode(null);
    setCutHoverPoint(null);
    cutHistoryRef.current = [];
  }, []);

  // Drop the entry at `deletedIdx` and shift every higher key down by one so
  // the layer-keyed maps stay aligned with the SVG's children array after
  // a layer is removed.
  const handleLayerDelete = useCallback((deletedIdx) => {
    const dropAndShift = (prev) => {
      const next = {};
      for (const [k, val] of Object.entries(prev)) {
        const idx = parseInt(k, 10);
        if (Number.isNaN(idx)) { next[k] = val; continue; }
        if (idx === deletedIdx) continue;
        next[idx > deletedIdx ? idx - 1 : idx] = val;
      }
      return next;
    };
    setLayerStitchSettings(dropAndShift);
    setLayerRails(dropAndShift);
    setLayerCutMarks(dropAndShift);
  }, []);

  const handleLayerInsert = useCallback((insertIdx, sourceIdx) => {
    const insertAndShift = (prev) => {
      const next = {};
      for (const [k, val] of Object.entries(prev)) {
        const idx = parseInt(k, 10);
        if (Number.isNaN(idx)) { next[k] = val; continue; }
        next[idx >= insertIdx ? idx + 1 : idx] = val;
      }
      if (prev[sourceIdx] != null) {
        next[insertIdx] = JSON.parse(JSON.stringify(prev[sourceIdx]));
      }
      return next;
    };
    // Update the synchronous refs too, so a follow-up call in the same tick
    // (e.g. setLayerStitchType clearing rails for a new Fill sublayer) composes
    // on top of the inserted state instead of reading stale pre-insert values.
    const nextSettings = insertAndShift(layerStitchSettingsRef.current);
    const nextRails = insertAndShift(layerRailsRef.current);
    layerStitchSettingsRef.current = nextSettings;
    layerRailsRef.current = nextRails;
    setLayerStitchSettings(nextSettings);
    setLayerRails(nextRails);
    setLayerCutMarks((prev) => {
      const next = {};
      for (const [k, val] of Object.entries(prev)) {
        const idx = parseInt(k, 10);
        if (Number.isNaN(idx)) { next[k] = val; continue; }
        next[idx >= insertIdx ? idx + 1 : idx] = val;
      }
      if (prev[sourceIdx] != null) {
        next[insertIdx] = JSON.parse(JSON.stringify(prev[sourceIdx]));
      }
      return next;
    });
  }, []);

  // Serialize all stitch state to a JSON-safe object for persistence
  const serializeStitchState = useCallback(() => {
    // Drop rails from layers that are explicitly Fill/Running so corruption
    // (stale/duplicate rails on a non-satin layer) is never persisted. Mirrors
    // the scrub done on hydrate; undefined/legacy types keep their rails.
    const cleanRails = {};
    for (const [k, v] of Object.entries(layerRails || {})) {
      const s = layerStitchSettings[k];
      const t =
        s?.passes?.[s?.activePassIndex || 0]?.stitchType ||
        s?.passes?.[0]?.stitchType ||
        s?.stitchType;
      if (!t || t === "satin") cleanRails[k] = v;
    }
    return {
      version: 1,
      layerStitchSettings,
      layerRails: cleanRails,
      layerCutMarks,
      hoopSettings: { selectedHoop, targetWidth, targetHeight },
    };
  }, [layerStitchSettings, layerRails, layerCutMarks, selectedHoop, targetWidth, targetHeight]);

  // ── Undo/Redo history ──────────────────────────────────────────────
  // Snapshot-based history of { layerStitchSettings, layerRails } captured
  // (debounced) on every meaningful state change. Cmd/Ctrl+Z and Cmd+Shift+Z
  // (or Ctrl+Y) walk back/forward through the stack.
  const HISTORY_LIMIT = 50;
  const HISTORY_DEBOUNCE_MS = 250;
  const colorGroupRef = useRef(null);
  const historyRef = useRef([]); // array of snapshots
  const historyIndexRef = useRef(-1); // pointer into historyRef
  const skipNextSnapshotRef = useRef(false); // guard while applying undo/redo or hydrate
  const debounceTimerRef = useRef(null);
  const [historyVersion, setHistoryVersion] = useState(0); // forces re-render when can(Undo|Redo) changes
  // Refs mirror the latest stitch state synchronously so immediate snapshots never
  // read stale closures (e.g. satin switch + auto-detect rails in one click).
  const layerStitchSettingsRef = useRef(layerStitchSettings);
  const layerRailsRef = useRef(layerRails);
  const layerCutMarksRef = useRef(layerCutMarks);
  layerCutMarksRef.current = layerCutMarks;
  layerStitchSettingsRef.current = layerStitchSettings;
  layerRailsRef.current = layerRails;

  const cloneState = (settings, rails, cutMarks = layerCutMarksRef.current) => ({
    layerStitchSettings: JSON.parse(JSON.stringify(settings || {})),
    layerRails: JSON.parse(JSON.stringify(rails || {})),
    layerCutMarks: JSON.parse(JSON.stringify(cutMarks || {})),
  });

  const pushStitchSnapshot = useCallback((settings, rails) => {
    const snapSettings = settings ?? layerStitchSettingsRef.current;
    const snapRails = rails ?? layerRailsRef.current;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const snap = cloneState(snapSettings, snapRails);
    const last = historyRef.current[historyIndexRef.current];
    const lastState = last?.redoState || last;
    if (lastState && JSON.stringify(cloneState(
      lastState.layerStitchSettings, lastState.layerRails, lastState.layerCutMarks
    )) === JSON.stringify(snap)) return false;
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push(snap);
    if (historyRef.current.length > HISTORY_LIMIT) {
      historyRef.current = historyRef.current.slice(-HISTORY_LIMIT);
    }
    historyIndexRef.current = historyRef.current.length - 1;
    setHistoryVersion((v) => v + 1);
    return true;
  }, []);

  // Apply settings and/or rails together, then push one history snapshot immediately.
  const recordStitchHistory = useCallback(({ settingsUpdater, railsUpdater } = {}) => {
    let nextSettings = layerStitchSettingsRef.current;
    let nextRails = layerRailsRef.current;
    if (settingsUpdater) nextSettings = settingsUpdater(nextSettings);
    if (railsUpdater) nextRails = railsUpdater(nextRails);

    skipNextSnapshotRef.current = true;
    layerStitchSettingsRef.current = nextSettings;
    layerRailsRef.current = nextRails;
    setLayerStitchSettings(nextSettings);
    setLayerRails(nextRails);
    pushStitchSnapshot(nextSettings, nextRails);
  }, [pushStitchSnapshot]);
  recordStitchHistoryRef.current = recordStitchHistory;

  // Record a snapshot (debounced). Truncates any redo branch first.
  useEffect(() => {
    if (skipNextSnapshotRef.current) {
      skipNextSnapshotRef.current = false;
      return;
    }
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      pushStitchSnapshot(layerStitchSettings, layerRails);
    }, HISTORY_DEBOUNCE_MS);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [layerStitchSettings, layerRails, layerCutMarks, pushStitchSnapshot]);

  const applySnapshot = useCallback((snap) => {
    if (!snap) return;
    skipNextSnapshotRef.current = true;
    snap = snap.redoState || snap;
    const nextSettings = snap.layerStitchSettings || {};
    const nextRails = snap.layerRails || {};
    layerCutMarksRef.current = snap.layerCutMarks || {};
    setLayerCutMarks(layerCutMarksRef.current);
    layerStitchSettingsRef.current = nextSettings;
    layerRailsRef.current = nextRails;
    setLayerStitchSettings(nextSettings);
    setLayerRails(nextRails);
    setHistoryVersion((v) => v + 1);
  }, []);

  const undoStitch = useCallback(() => {
    colorGroupRef.current = null;
    if (historyIndexRef.current <= 0) return false;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const leaving = historyRef.current[historyIndexRef.current];
    historyIndexRef.current -= 1;
    // svgLayerDelete markers store pre/post stitch state inline
    if (leaving?.type === 'svgLayerDelete' || leaving?.type === 'svgLayerReorder') {
      applySnapshot(leaving.undoState);
      return leaving.type;
    }
    applySnapshot(historyRef.current[historyIndexRef.current]);
    // Signal caller to also undo the SVG layer change made by the Cut tool
    if (leaving?.type === 'svgCut' || leaving?.type === 'svgColor') return leaving.type;
    return true;
  }, [applySnapshot]);

  const redoStitch = useCallback(() => {
    colorGroupRef.current = null;
    if (historyIndexRef.current >= historyRef.current.length - 1) return false;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    historyIndexRef.current += 1;
    const arriving = historyRef.current[historyIndexRef.current];
    if (arriving?.type === 'svgLayerDelete' || arriving?.type === 'svgLayerReorder') {
      applySnapshot(arriving.redoState);
      setHistoryVersion((v) => v + 1);
      return arriving.type;
    }
    if (arriving?.type === 'svgColor') {
      applySnapshot(arriving);
      return 'svgColor';
    }
    if (arriving?.type === 'svgCut') {
      setHistoryVersion((v) => v + 1);
      return 'svgCut';
    }
    applySnapshot(arriving);
    return true;
  }, [applySnapshot]);

  // Push a marker into the stitch history so that undoStitch/redoStitch know
  // to also call SVG undo/redo when stepping over a Cut Shape operation.
  const pushSvgCutMarker = useCallback(() => {
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push({ ...cloneState(layerStitchSettingsRef.current, layerRailsRef.current), type: 'svgCut' });
    if (historyRef.current.length > HISTORY_LIMIT) {
      historyRef.current = historyRef.current.slice(-HISTORY_LIMIT);
    }
    historyIndexRef.current = historyRef.current.length - 1;
    setHistoryVersion((v) => v + 1);
  }, []);

  // A continuous picker interaction replaces its last SVG history entry. A new
  // interaction, layer, stitch edit or undo/redo always starts a separate step.
  const pushSvgColorMarker = useCallback((layerIdx, group) => {
    const last = historyRef.current[historyIndexRef.current];
    const state = cloneState(layerStitchSettingsRef.current, layerRailsRef.current);
    const sameState = last && JSON.stringify(cloneState(
      last.layerStitchSettings, last.layerRails, last.layerCutMarks
    )) === JSON.stringify(state);
    if (group && colorGroupRef.current === group && last?.type === 'svgColor'
        && last.layerIdx === layerIdx && sameState
        && historyIndexRef.current === historyRef.current.length - 1) {
      return true;
    }
    // Preserve any stitch edit whose debounce has not fired yet.
    pushStitchSnapshot();
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push({ ...state, type: 'svgColor', layerIdx });
    historyRef.current = historyRef.current.slice(-HISTORY_LIMIT);
    historyIndexRef.current = historyRef.current.length - 1;
    colorGroupRef.current = group;
    setHistoryVersion((v) => v + 1);
    return false;
  }, [pushStitchSnapshot]);

  // A layer move changes both histories. Restore artwork and all indexed
  // settings together so Undo cannot assign satin rails to another color.
  const handleLayerReorder = useCallback((fromIdx, toIdx) => {
    if (!Number.isInteger(fromIdx) || !Number.isInteger(toIdx) ||
        fromIdx < 0 || toIdx < 0 || fromIdx >= numLayers || toIdx >= numLayers || fromIdx === toIdx) return;
    pushStitchSnapshot();
    const before = cloneState(layerStitchSettingsRef.current, layerRailsRef.current);
    const remap = (map) => Object.fromEntries(Object.entries(map).map(([key, value]) => {
      const idx = Number(key);
      const next = idx === fromIdx ? toIdx
        : fromIdx < toIdx && idx > fromIdx && idx <= toIdx ? idx - 1
        : fromIdx > toIdx && idx >= toIdx && idx < fromIdx ? idx + 1 : idx;
      return [Number.isNaN(idx) ? key : next, value];
    }));
    const after = {
      layerStitchSettings: remap(before.layerStitchSettings),
      layerRails: remap(before.layerRails),
      layerCutMarks: remap(before.layerCutMarks),
    };
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push({ type: 'svgLayerReorder', undoState: before, redoState: after });
    historyRef.current = historyRef.current.slice(-HISTORY_LIMIT);
    historyIndexRef.current = historyRef.current.length - 1;
    colorGroupRef.current = null;
    applySnapshot(after);
  }, [numLayers, pushStitchSnapshot, applySnapshot]);

  // Push a layer-delete marker that stores both undo (pre-delete) and redo
  // (post-delete) stitch state so a single Cmd+Z restores both the SVG layer
  // and the stitch settings simultaneously.
  const pushSvgLayerDeleteMarker = useCallback((deletedIdx, preSettings, preRails) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    skipNextSnapshotRef.current = true;
    const dropAndShift = (prev) => {
      const next = {};
      for (const [k, val] of Object.entries(prev || {})) {
        const idx = parseInt(k, 10);
        if (Number.isNaN(idx)) { next[k] = val; continue; }
        if (idx === deletedIdx) continue;
        next[idx > deletedIdx ? idx - 1 : idx] = val;
      }
      return next;
    };
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push({
      type: 'svgLayerDelete',
      undoState: cloneState(preSettings || {}, preRails || {}),
      redoState: cloneState(dropAndShift(preSettings), dropAndShift(preRails), dropAndShift(layerCutMarksRef.current)),
    });
    if (historyRef.current.length > HISTORY_LIMIT) {
      historyRef.current = historyRef.current.slice(-HISTORY_LIMIT);
    }
    historyIndexRef.current = historyRef.current.length - 1;
    setHistoryVersion((v) => v + 1);
  }, []);

  const resetStitchHistory = useCallback(() => {
    historyRef.current = [];
    historyIndexRef.current = -1;
    skipNextSnapshotRef.current = true;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setHistoryVersion((v) => v + 1);
  }, []);

  // Hydrate state from a previously serialized snapshot
  const hydrateStitchState = useCallback((snapshot) => {
    if (!snapshot || typeof snapshot !== "object") return;
    skipNextSnapshotRef.current = true;
    const hydratedSettings = snapshot.layerStitchSettings && typeof snapshot.layerStitchSettings === "object"
      ? snapshot.layerStitchSettings
      : {};
    const rawRails = snapshot.layerRails && typeof snapshot.layerRails === "object"
      ? snapshot.layerRails
      : {};
    const hydratedCutMarks = snapshot.layerCutMarks && typeof snapshot.layerCutMarks === "object"
      ? snapshot.layerCutMarks
      : {};
    // Scrub stale rails: a layer that's explicitly Fill/Running can't be satin,
    // so any rails on it are leftover corruption (e.g. a duplicate from an older
    // reorder bug, or a sublayer cloned from a satin parent). Drop them on load so
    // they can't resurface if the layer is later switched to satin. An
    // undefined/legacy type keeps its rails for backward compatibility.
    const hydratedRails = {};
    for (const [k, v] of Object.entries(rawRails)) {
      const s = hydratedSettings[k];
      const t =
        s?.passes?.[s?.activePassIndex || 0]?.stitchType ||
        s?.passes?.[0]?.stitchType ||
        s?.stitchType;
      if (!t || t === "satin") hydratedRails[k] = v;
    }
    setLayerStitchSettings(hydratedSettings);
    setLayerRails(hydratedRails);
    setLayerCutMarks(hydratedCutMarks);
    layerCutMarksRef.current = hydratedCutMarks;
    layerStitchSettingsRef.current = hydratedSettings;
    layerRailsRef.current = hydratedRails;
    if (snapshot.hoopSettings) {
      const hs = snapshot.hoopSettings;
      if (typeof hs.selectedHoop === "number") setSelectedHoop(hs.selectedHoop);
      if (typeof hs.targetWidth === "number") setTargetWidth(hs.targetWidth);
      if (typeof hs.targetHeight === "number") setTargetHeight(hs.targetHeight);
    }
    // Reset history so the hydrated state becomes the baseline (index 0).
    // This way the user's first edit produces history=[baseline, edit] and undo is possible.
    historyRef.current = [cloneState(hydratedSettings, hydratedRails, hydratedCutMarks)];
    historyIndexRef.current = 0;
    setHistoryVersion((v) => v + 1);
  }, []);

  // Reading historyVersion ensures consumers re-render when the history changes
  const canUndoStitch = historyVersion >= 0 && historyIndexRef.current > 0;
  const canRedoStitch = historyVersion >= 0 && historyIndexRef.current < historyRef.current.length - 1;

  // Memoize the return object so consumers only re-render when the actual
  // content changes — not on every parent render cycle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({
    embroideryMode,
    setEmbroideryMode,
    layerStitchSettings,
    layerRails,
    layerCutMarks,
    setLayerRails,
    setLayerCutMarksForLayer,
    getLayerSettings,
    updateLayerSettings,
    setLayerStitchType,
    addPass,
    removePass,
    updatePassSettings,
    setActivePass,
    setLayerRailPath,
    getRails,
    activeRailDrawing,
    startRailDrawing,
    addRailPoint,
    finishRailDrawing,
    cancelRailDrawing,
    splitMode,
    splitHoverPoint,
    startSplitMode,
    setSplitPoint,
    cancelSplitMode,
    updateSplitHover,
    getSplitSubpaths,
    removeLastRailPair,
    removeRailPair,
    cutMode,
    cutHoverPoint,
    startCutMode,
    setCutPoint,
    updateCutHover,
    undoLastCut,
    finishCutMode,
    cancelCutMode,
    handleLayerReorder,
    handleLayerDelete,
    handleLayerInsert,
    DEFAULT_LAYER_SETTINGS,
    serializeStitchState,
    hydrateStitchState,
    undoStitch,
    redoStitch,
    pushStitchSnapshot,
    recordStitchHistory,
    pushSvgCutMarker,
    pushSvgColorMarker,
    pushSvgLayerDeleteMarker,
    canUndoStitch,
    canRedoStitch,
    resetStitchHistory,
    selectedHoop, setSelectedHoop,
    targetWidth, setTargetWidth,
    targetHeight, setTargetHeight,
  }), [
    embroideryMode, layerStitchSettings, layerRails, layerCutMarks, activeRailDrawing, splitMode,
    splitHoverPoint, cutMode, cutHoverPoint, canUndoStitch, canRedoStitch,
    selectedHoop, targetWidth, targetHeight,
    setEmbroideryMode, setLayerRails, setLayerCutMarksForLayer, getLayerSettings, updateLayerSettings,
    setLayerStitchType, addPass, removePass, updatePassSettings, setActivePass,
    setLayerRailPath, getRails, startRailDrawing, addRailPoint, finishRailDrawing,
    cancelRailDrawing, startSplitMode, setSplitPoint, cancelSplitMode, updateSplitHover,
    getSplitSubpaths, removeLastRailPair, startCutMode, setCutPoint, updateCutHover,
    undoLastCut, finishCutMode, cancelCutMode, handleLayerReorder, handleLayerDelete, handleLayerInsert,
    serializeStitchState,
    hydrateStitchState, undoStitch, redoStitch, pushStitchSnapshot, recordStitchHistory, pushSvgCutMarker, pushSvgColorMarker, pushSvgLayerDeleteMarker, resetStitchHistory,
  ]);
}
